import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcRegistrationV1AccountQueryServiceAccountGet } from '@interxfi/api-client/generated/hooks/ixc-registration-v1-account-query-service-account-get';
import { PageLayout } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { Hint } from '@interxfi/design-system/components/hint/hint';
import InfoIcon from '@interxfi/design-system/icons/react/icon-info';
import { IxcRegistrationV1ControlsMarginType } from '@interxfi/types';
import { AccountMarginTypePill } from '../../../../../../../../../features/registration/account/account-margin-type-pill';
import { AccountRegistrationOrPurposePill } from '../../../../../../../../../features/registration/account/account-registration-or-purpose-pill';
import { AccountStatusPill } from '../../../../../../../../../features/registration/account/account-status-pill';
import { usePortfolioPerformanceSummary } from '../../../../../../../../../features/registration/account/portfolio-value/account-portfolio-performance';
import { DividendsReceivableByAssetTable } from '../../../../../../../../../features/registration/account/portfolio-value/dividends-receivable/dividends-receivable-by-asset-table';
import { RegistrationAccountPageHeader } from '../../../../../../../../../features/registration/account/registration-account-page-header';
import { getPageHead } from '../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/registration/account/$accountId/_account/dividends-receivable/_dividends-receivable/')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `dividends-receivable-${params.accountId}`,
        label: 'Dividends Receivable',
        to: `/registration/account/${params.accountId}/dividends-receivable`,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Account Dividends Receivable'),
  component: AccountDividendsReceivableDetail,
});

const stableEmptyArray: never[] = [];

function AccountDividendsReceivableDetail() {
  const { accountId } = Route.useParams();
  const { data, isPending, error } = useIxcRegistrationV1AccountQueryServiceAccountGet({ accountId });
  const accountDataPending = Boolean(isPending || error);
  const query = usePortfolioPerformanceSummary(accountId);
  const rows = useMemo(
    () => Object.entries(query.data?.unrealizedDividends?.byAsset || {}) || stableEmptyArray,
    [query.data?.unrealizedDividends?.byAsset],
  );

  return (
    <PageLayout gap={7}>
      <PageLayout.Section noBorder>
        <RegistrationAccountPageHeader
          accountId={accountId}
          pills={
            <>
              <AccountStatusPill isPending={accountDataPending} size="large" status={data?.account?.status} />
              <AccountRegistrationOrPurposePill account={data?.account} isPending={accountDataPending} size="large" />
              <AccountMarginTypePill
                isPending={accountDataPending}
                marginType={data?.account?.data?.controls?.registration?.settings?.marginType || IxcRegistrationV1ControlsMarginType.Cash}
                size="large"
              />
            </>
          }
        />
      </PageLayout.Section>

      <PageLayout.Section noBorder>
        <Hint icon={InfoIcon} size="small" withBorderRadius>
          Dividends Receivable are unrealized dividends to be paid on the payment date shown in the table below. They are not included as part of the
          Portfolio Value total.
        </Hint>
      </PageLayout.Section>

      <PageLayout.Section>
        <DividendsReceivableByAssetTable data={rows} />
      </PageLayout.Section>
    </PageLayout>
  );
}
